import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Typography, Tooltip, Divider } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorageIcon from '@mui/icons-material/Storage';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { api } from '../api/client.js';
import { DB_ENVIRONMENTS, dbEnvironmentLabel, environmentForHostname, type DbProfile } from '../../shared/dbEnvironments.js';
import { colors } from '../styles/theme.js';

/** Signal aus dem Admin-Bereich, dass sich die eigene Datenbank-Wahl geändert hat. */
export const DB_PROFILE_CHANGED_EVENT = 'tracking-scanner:db-profile-changed';

/**
 * Umgebungs-Umschalter in der Menüleiste (23.09.2026).
 *
 * Zeigt, in WELCHER Umgebung man gerade arbeitet — abgelesen am Hostnamen
 * (int.screener…, sta.screener…, screener…) — und bietet die anderen als
 * Link an: ein Klick öffnet den Screener dieser Umgebung, dort gilt die
 * eigene Anmeldung. Das ersetzt die Datenbank-Umschaltung an dieser Stelle:
 * ein deployter Dienst kennt nur seine eigene Datenbank, die anderen waren
 * hier immer ausgegraut.
 *
 * Auf localhost gibt es keine Umgebung: die Leiste zeigt „Lokal" und dazu die
 * Datenbank, gegen die das lokale Backend gerade läuft. Gewechselt wird die
 * weiterhin unter Admin → Settings → Allgemein.
 *
 * NUR FÜR ADMINS — die Komponente wird für andere Rollen gar nicht erst
 * gerendert (siehe Layout).
 */
export default function DbEnvironmentBadge() {
  const navigate = useNavigate();
  const current = useMemo(() => environmentForHostname(window.location.hostname), []);
  const [dbProfile, setDbProfile] = useState<DbProfile | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    // Deployt IST die Datenbank die Umgebung — nur lokal lohnt der Blick.
    if (current) return;
    const load = () => api
      .get<{ active_profile: DbProfile }>('/admin/database-profile')
      .then((res) => { if (res.success) setDbProfile(res.data.active_profile); });
    load();
    window.addEventListener(DB_PROFILE_CHANGED_EVENT, load);
    return () => window.removeEventListener(DB_PROFILE_CHANGED_EVENT, load);
  }, [current]);

  const label = current ? current.label : 'Lokal';
  const color = current ? current.color : colors.silver;
  const dbLabel = dbProfile ? dbEnvironmentLabel(dbProfile) : null;

  const shell = {
    textTransform: 'none' as const,
    color: colors.white,
    bgcolor: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: 2,
    px: 1.25,
    py: 0.5,
    mr: 1,
    minWidth: 0,
  };

  return (
    <>
      <Tooltip title="Umgebung wechseln">
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={(
            <KeyboardArrowDownIcon
              sx={{ color: colors.silver, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          )}
          sx={{ ...shell, gap: 0.5, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
            <PublicIcon sx={{ fontSize: 16, color: colors.silver }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: colors.white }}>{label}</Typography>
            {!current && dbLabel && (
              <Typography variant="caption" sx={{ color: colors.silver }}>· DB {dbLabel}</Typography>
            )}
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 320, mt: 1, borderRadius: 1.5 } } }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: colors.black60 }}>
          {current
            ? `Du arbeitest in ${current.label} — ein Klick öffnet den Screener der anderen Umgebung`
            : 'Lokale Entwicklung — ein Klick öffnet den Screener der Umgebung'}
        </Typography>
        <Divider />

        {DB_ENVIRONMENTS.map((e) => {
          const active = current?.profile === e.profile;
          return (
            <MenuItem
              key={e.profile}
              component="a"
              href={e.appUrl}
              selected={active}
              disabled={active}
              sx={{ py: 1.25, gap: 1.5, alignItems: 'flex-start' }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: e.color, mt: 0.75, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: active ? 600 : 500 }}>{e.label}</Typography>
                <Typography variant="caption" sx={{ color: colors.black60, display: 'block' }}>
                  {new URL(e.appUrl).hostname} · {e.hint}
                </Typography>
              </Box>
              {active
                ? <CheckIcon fontSize="small" sx={{ mt: 0.5, color: colors.black60 }} />
                : <OpenInNewIcon fontSize="small" sx={{ mt: 0.5, color: colors.black60 }} />}
            </MenuItem>
          );
        })}

        <Divider />
        {current ? (
          <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: colors.black60 }}>
            Die Anmeldung gilt je Umgebung — dort gegebenenfalls neu anmelden.
          </Typography>
        ) : (
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/admin/settings/general'); }} sx={{ py: 1.25, gap: 1.5 }}>
            <StorageIcon fontSize="small" sx={{ color: colors.black60 }} />
            <Box>
              <Typography variant="body2">Datenbank des lokalen Backends{dbLabel ? `: ${dbLabel}` : ''}</Typography>
              <Typography variant="caption" sx={{ color: colors.black60 }}>wechseln unter Admin → Settings → Allgemein</Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
